import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Calendar, Clock, CheckCircle, PlayCircle, Bell } from 'lucide-react';
import { ClientScheduleData, BillingRuleEvent, ReminderScheduleData } from '../types/schedule';
import { ActiveTab } from '../hooks/useScheduleData';

interface ClientScheduleCardProps {
  clientData?: ClientScheduleData;
  reminderData?: ReminderScheduleData;
  day?: number;
  currentDate?: { year: number; month: number };
  onStatusChange?: (clientId: number, eventId: number, eventDate: string, field: 'inProgress' | 'completed', value: boolean) => void;
  updatingStatus?: {[key: string]: boolean};
  activeTab?: ActiveTab;
}

const ClientScheduleCard: React.FC<ClientScheduleCardProps> = ({
  clientData,
  reminderData,
  day,
  currentDate,
  onStatusChange,
  updatingStatus = {},
  activeTab = 'COBRANÇA'
}) => {
  // Se for aba de lembretes, usar reminderData
  if (activeTab === 'LEMBRETE' && reminderData) {
    const { client, reminders } = reminderData;
    const hasReminders = reminders && reminders.length > 0;

    // Formatar horário
    const formatTime = (time: string | null) => {
      if (!time) return 'Não configurado';
      const [hours, minutes] = time.split(':');
      return `${hours}:${minutes}`; // Retorna apenas HH:mm
    };

    return (
      <Card 
        className={`
          transition-all duration-200 hover:shadow-md
          ${hasReminders 
            ? 'bg-white border-l-4 border-l-orange-500 shadow-sm' 
            : 'bg-gray-50 border-gray-200 opacity-75'
          }
        `}
      >
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-semibold text-sm">
                  {(client.trade_name || client.legal_name).charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-800">{client.trade_name || client.legal_name}</div>
                <div className="text-xs text-gray-500">{client.email}</div>
              </div>
            </div>
            {hasReminders && (
              <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-200">
                {reminders.length} lembrete{reminders.length !== 1 ? 's' : ''}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>

        <CardContent className="pt-0">
          {hasReminders ? (
            <div className="space-y-3">
              {reminders.map((reminder) => (
                <div
                  key={reminder.id}
                  className="border rounded-lg p-3 bg-orange-50 border-orange-200"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Bell className="text-orange-600" />
                      <Badge className="text-xs bg-orange-100 text-orange-800 border-orange-200">
                        LEMBRETE
                      </Badge>
                    </div>
                    <div className="text-xs text-gray-500">
                      {reminder.is_active ? (
                        <span className="text-green-600 font-semibold">Ativo</span>
                      ) : (
                        <span className="text-gray-400">Inativo</span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-1 text-sm">
                    <div className="flex items-center gap-2 text-gray-700">
                      <span className="font-medium">Dias antes:</span>
                      <span className="text-orange-600 font-semibold">
                        {reminder.days_before_due} dia{reminder.days_before_due !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <Clock className="h-3 w-3 text-gray-500" />
                      <span className="font-medium">Horário de início:</span>
                      <span className="text-gray-600">{formatTime(reminder.window_start)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <div className="text-gray-400 mb-2">
                <Bell className="h-8 w-8 mx-auto" />
              </div>
              <p className="text-sm text-gray-500">
                Nenhum lembrete ativo configurado
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Lógica original para cobranças
  if (!clientData || !day || !currentDate) {
    return null;
  }

  const { client, events } = clientData;
  const dayEvents = events[day] || [];

  // Verificar se o cliente tem eventos no dia
  const hasEvents = dayEvents.length > 0;

  // Obter cor do badge baseado no tipo de evento
  const getEventBadgeColor = (event: string) => {
    switch (event.toUpperCase()) {
      case 'COBRANÇA':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'REVISTORIA':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'LEMBRETE':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'PROMOÇÃO':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Obter ícone baseado no tipo de evento
  const getEventIcon = (event: string) => {
    switch (event.toUpperCase()) {
      case 'COBRANÇA':
        return '💰';
      case 'REVISTORIA':
        return '🔍';
      case 'LEMBRETE':
        return '⏰';
      case 'PROMOÇÃO':
        return '🎯';
      default:
        return '📋';
    }
  };

  // Formatar data de vencimento
  const formatDueDay = (dueDay: number, billingMonth: string) => {
    try {
      // Obter data atual
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth(); // 0-11
      
      let targetYear = currentYear;
      let targetMonth = currentMonth;
      
      // Calcular mês baseado no billing_month
      switch (billingMonth) {
        case 'atual':
          // Mês atual
          break;
        case '+1':
          // Próximo mês
          targetMonth = currentMonth + 1;
          if (targetMonth > 11) {
            targetMonth = 0;
            targetYear = currentYear + 1;
          }
          break;
        case '-1':
          // Mês anterior
          targetMonth = currentMonth - 1;
          if (targetMonth < 0) {
            targetMonth = 11;
            targetYear = currentYear - 1;
          }
          break;
        case '-2':
          // Dois meses atrás
          targetMonth = currentMonth - 2;
          if (targetMonth < 0) {
            targetMonth = 12 + targetMonth;
            targetYear = currentYear - 1;
          }
          break;
        default:
          console.warn('Billing month desconhecido:', billingMonth);
          return 'Data inválida';
      }
      
      // Criar data com o dia de vencimento
      const date = new Date(targetYear, targetMonth, dueDay);
      
      // Verificar se a data é válida
      if (isNaN(date.getTime())) {
        return 'Data inválida';
      }
      
      return date.toLocaleDateString('pt-BR', { 
        day: '2-digit', 
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return 'Data inválida';
    }
  };

  return (
    <Card 
      className={`
        transition-all duration-200 hover:shadow-md
        ${hasEvents 
          ? 'bg-white border-l-4 border-l-green-500 shadow-sm' 
          : 'bg-gray-50 border-gray-200 opacity-75'
        }
      `}
    >
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 font-semibold text-sm">
                {(client.trade_name || client.legal_name).charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-800">{client.trade_name || client.legal_name}</div>
              <div className="text-xs text-gray-500">{client.email}</div>
            </div>
          </div>
          {hasEvents && (
            <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
              {dayEvents.length} evento{dayEvents.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>

      <CardContent className="pt-0">
        {hasEvents ? (
          <div className="space-y-3">
            {dayEvents.map((event: BillingRuleEvent) => {
              // Calcular a data do evento
              const eventDate = `${currentDate.year}-${currentDate.month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
              
              // Verificar se o evento é do dia atual ou anterior
              const today = new Date();
              const eventDateObj = new Date(currentDate.year, currentDate.month - 1, day);
              const isToday = eventDateObj.toDateString() === today.toDateString();
              const isPastOrToday = eventDateObj <= today;
              
              // Obter status do evento (agora vem do backend)
              const status = event.status ? {
                inProgress: event.status.is_in_progress || false,
                completed: event.status.is_finalized || false
              } : { inProgress: false, completed: false };

              // Verificar se está sendo atualizado
              const statusKey = `${client.id}-${event.id}-${eventDate}`;
              const isUpdating = updatingStatus[statusKey] || false;
              
              return (
                <div
                  key={event.id}
                  className="border rounded-lg p-3 bg-gray-50"
                >
                  {/* Cabeçalho do evento */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getEventIcon(event.event)}</span>
                      <Badge 
                        className={`text-xs ${getEventBadgeColor(event.event)}`}
                      >
                        {event.event}
                      </Badge>
                    </div>
                    <div className="text-xs text-gray-500">
                      Vence: {formatDueDay(event.due_day, event.billing_month)}
                    </div>
                  </div>

                  {/* Checkboxes de status */}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`inProgress-${event.id}`}
                        checked={status.inProgress}
                        disabled={isUpdating || !isPastOrToday}
                        onCheckedChange={(checked) => 
                          onStatusChange(client.id, event.id, eventDate, 'inProgress', checked as boolean)
                        }
                        className="data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500 disabled:opacity-50"
                      />
                      <label 
                        htmlFor={`inProgress-${event.id}`}
                        className={`text-sm font-medium flex items-center gap-1 ${
                          !isPastOrToday ? 'text-gray-400' : 'text-gray-700'
                        }`}
                      >
                        <PlayCircle className={`h-4 w-4 ${!isPastOrToday ? 'text-gray-400' : 'text-blue-500'}`} />
                        Em andamento
                      </label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`completed-${event.id}`}
                        checked={status.completed}
                        disabled={isUpdating || !isPastOrToday}
                        onCheckedChange={(checked) => 
                          onStatusChange(client.id, event.id, eventDate, 'completed', checked as boolean)
                        }
                        className="data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500 disabled:opacity-50"
                      />
                      <label 
                        htmlFor={`completed-${event.id}`}
                        className={`text-sm font-medium flex items-center gap-1 ${
                          !isPastOrToday ? 'text-gray-400' : 'text-gray-700'
                        }`}
                      >
                        <CheckCircle className={`h-4 w-4 ${!isPastOrToday ? 'text-gray-400' : 'text-green-500'}`} />
                        Finalizado
                      </label>
                    </div>
                  </div>

                  {/* Informações adicionais */}
                  <div className="mt-2 text-xs text-gray-500 flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Dia {day}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Criado em {new Date(event.created_at).toLocaleDateString('pt-BR')}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-4">
            <div className="text-gray-400 mb-2">
              <Calendar className="h-8 w-8 mx-auto" />
            </div>
            <p className="text-sm text-gray-500">
              Nenhum evento programado para o dia {day}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ClientScheduleCard;
